/**
 * Number and date formatting for charts. Everything goes through `Intl` with
 * an explicit locale, so axis ticks, tooltips, tables and text summaries agree.
 *
 * Pass `locale` when rendering on the server: without it the runtime default
 * is used, which can differ between the server and the browser.
 */
const numberCache = new Map();
const dateCache = new Map();
function numberFormat(locale, options) {
    const key = `${locale ?? ''}|${JSON.stringify(options)}`;
    let f = numberCache.get(key);
    if (!f) {
        f = new Intl.NumberFormat(locale, options);
        numberCache.set(key, f);
    }
    return f;
}
function dateFormat(locale, options) {
    const key = `${locale ?? ''}|${JSON.stringify(options)}`;
    let f = dateCache.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, options);
        dateCache.set(key, f);
    }
    return f;
}
/** Full-precision value formatter (tooltips, tables, summaries). */
export function createValueFormatter(locale, options = { maximumFractionDigits: 2 }, custom) {
    if (custom)
        return custom;
    const f = numberFormat(locale, options);
    return (v) => f.format(v);
}
/**
 * Axis tick formatter: compact notation (1.2K, 3.4M) unless the caller set a
 * style, so long numbers never crowd the axis. A custom formatter wins.
 */
export function createTickFormatter(locale, options, custom) {
    if (custom)
        return custom;
    const f = numberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
        ...options,
    });
    return (v) => f.format(v);
}
const DEFAULT_DATE = { month: 'short', day: 'numeric' };
/** Formats an x value: dates with `Intl.DateTimeFormat`, numbers with `Intl.NumberFormat`. */
export function createXFormatter(locale, dateOptions = DEFAULT_DATE, custom) {
    if (custom)
        return custom;
    const d = dateFormat(locale, dateOptions);
    const n = numberFormat(locale, { maximumFractionDigits: 2 });
    return (value) => {
        if (value instanceof Date)
            return d.format(value);
        if (typeof value === 'number')
            return n.format(value);
        return String(value);
    };
}
//# sourceMappingURL=format.js.map